import { authHeader, handleResponse } from './helpers';

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;
const TENANTS_URL = `${API_BASE_URL}/tenants/tenants/`;

// LIST (optionally with query params: page, search, ordering, etc.)
export const listTenants = async (params = {}) => {
  const url = new URL(TENANTS_URL);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, v);
  });

  const res = await fetch(url, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to list tenants');
};

// CREATE
export const createTenant = async (data) => {
  const res = await fetch(TENANTS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(data),
  });
  return handleResponse(res, 'Failed to create tenant');
};

// RETRIEVE
export const getTenant = async (tenantId) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/`, {
    headers: { ...authHeader() },
  });
  return handleResponse(res, 'Failed to fetch tenant');
};

// UPDATE (PUT) - full replace (optional)
export const updateTenant = async (tenantId, data) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(data),
  });
  return handleResponse(res, 'Failed to update tenant');
};

// PARTIAL UPDATE (PATCH)
export const patchTenant = async (tenantId, data) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(data),
  });
  return handleResponse(res, 'Failed to patch tenant');
};

// DELETE
export const deleteTenant = async (tenantId) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  return handleResponse(res, 'Failed to delete tenant');
};

export const updateTenantPublicWithFiles = async (tenantId, data = {}) => {
  const url = `${TENANTS_URL}${tenantId}/update_public/`;
  const form = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });

  const res = await fetch(url, {
    method: "PATCH",
    headers: { ...authHeader() }, // let browser set boundary
    body: form,
  });

  return handleResponse(res, "Failed to update tenant public info (multipart)");
};

export const updateTenantPublic = async (tenantId, data = {}) => {
  const url = `${TENANTS_URL}${tenantId}/update_public/`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(data),
  });
  return handleResponse(res, "Failed to update tenant public info");
};

// ACTIVATE
export const activateTenant = async (tenantId) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/activate/`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return handleResponse(res, 'Failed to activate tenant');
};

// DEACTIVATE (optional reason)
export const deactivateTenant = async (tenantId, reason = '') => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/deactivate/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(reason ? { reason } : {}),
  });
  return handleResponse(res, 'Failed to deactivate tenant');
};
// LOGS (filters supported: event_type, status, from, to, limit)
export const getTenantLogs = async (tenantId, params = {}) => {
  const url = new URL(`${TENANTS_URL}${tenantId}/logs/`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, v);
  });

  const res = await fetch(url, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to fetch tenant logs');
};

// STATS (days=1..365)
export const getTenantStats = async (tenantId, days = 30) => {
  const url = new URL(`${TENANTS_URL}${tenantId}/stats/`);
  url.searchParams.append('days', String(days));

  const res = await fetch(url, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to fetch tenant stats');
};

export const setTenantTrialExpiration = async (tenantId, trial_expires_at) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/set-trial-expiration/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ trial_expires_at }),
  });
  return handleResponse(res, "Failed to set trial expiration");
};

// ONBOARDING: vyhľadanie firmy v RPO podľa názvu (od 3 znakov)
export const lookupCompany = async (q) => {
  const url = new URL(`${TENANTS_URL}lookup-company/`);
  url.searchParams.append('q', q);
  const res = await fetch(url, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to lookup company');
};

// ONBOARDING: detail firmy (finstat.sk) podľa IČO -> DIČ, IČ DPH, adresa sídla
export const lookupCompanyDetail = async (ico) => {
  const url = new URL(`${TENANTS_URL}company-detail/`);
  url.searchParams.append('ico', ico);
  const res = await fetch(url, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to lookup company detail');
};

// ONBOARDING: poslať ponuku (e-mail + cenník PDF) kontaktnej osobe organizácie
export const sendOffer = async (tenantId) => {
  const res = await fetch(`${TENANTS_URL}${tenantId}/send-offer/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to send offer');
};

// ONBOARDING LEADY (nevytvárajú reálnu organizáciu)
const LEADS_URL = `${API_BASE_URL}/tenants/onboarding-leads/`;

export const listLeads = async () => {
  const res = await fetch(LEADS_URL, { headers: { ...authHeader() } });
  return handleResponse(res, 'Failed to list onboarding leads');
};

export const createLead = async (data) => {
  const res = await fetch(LEADS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(data),
  });
  return handleResponse(res, 'Failed to create onboarding lead');
};

export const deleteLead = async (id) => {
  const res = await fetch(`${LEADS_URL}${id}/`, { method: 'DELETE', headers: { ...authHeader() } });
  if (res.status === 204) return true;
  return handleResponse(res, 'Failed to delete onboarding lead');
};

export const sendLeadOffer = async (id) => {
  const res = await fetch(`${LEADS_URL}${id}/send-offer/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to send offer');
};

export const sendLeadContract = async (id, license, extra = {}) => {
  const res = await fetch(`${LEADS_URL}${id}/send-contract/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ license, ...extra }),
  });
  return handleResponse(res, 'Failed to send contract');
};

export const acceptLeadContract = async (id) => {
  const res = await fetch(`${LEADS_URL}${id}/accept-contract/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to accept contract');
};

export const uploadLeadContract = async (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${LEADS_URL}${id}/upload-contract/`, {
    method: 'POST',
    headers: { ...authHeader() }, // bez Content-Type -> browser nastaví multipart boundary
    body: fd,
  });
  return handleResponse(res, 'Failed to upload contract');
};

export const downloadLeadContract = async (id, filename) => {
  const res = await fetch(`${LEADS_URL}${id}/download-contract/`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'zmluva';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const createLeadAccess = async (id) => {
  const res = await fetch(`${LEADS_URL}${id}/create-access/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to create access');
};

export const fetchInbound = async () => {
  const res = await fetch(`${LEADS_URL}fetch-inbound/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to fetch inbound');
};

export const markLeadMessagesSeen = async (id) => {
  const res = await fetch(`${LEADS_URL}${id}/mark-messages-seen/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse(res, 'Failed to mark messages seen');
};